import { PocBackdrop } from 'commons/components/components-wrapper/poc-backdrop.component';
import { PocProgressSpinner } from 'commons/components/components-wrapper/poc-progress-spinner.component';
import { useAuthContext } from 'core/auth/hooks/use-auth-context.hook';
import { GENERIC_SERVER_ERROR_TRANSLATION } from 'core/constants/constants';
import { usePocMutation } from 'core/custom-hooks/use-poc-mutation.hook';
import { useTranslation } from 'core/i18n/i18n';
import { executeRequest } from 'core/utils/backend-service';
import { showErrorNotification } from 'core/utils/notification-service';
import { settingsModulePaths } from 'core/routes/routes';
import { useEffect, useState } from 'react';
import { usePopupManager } from 'react-popup-manager';
import { useLocation, useNavigate } from 'react-router-dom';
import AboutScreen from './about-box/about-screen.component';
import { HeaderApp } from './app-header.component';
import { useLanguages } from './hooks/use-languages.hook';
import { useUserAssistance } from './hooks/use-user-assistance/use-user-assistance.hook';

export const HeaderAppContainer: React.FC = () => {
    const [isPageLoading, setIsPageLoading] = useState<boolean>(false);
    const { auth } = useAuthContext();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const popupManager = usePopupManager();

    const { languages, isError: hasLanguagesError } = useLanguages();
    const {
        handleClickUserAssistance,
        isCheckingUserAssistanceAvailability,
        isUserAssistanceAvailable,
        userAssistanceTooltipKey,
    } = useUserAssistance();

    const { mutate: signOut } = usePocMutation({
        mutationFn: () => executeRequest(() => auth.signOutRedirect()),
        onSettled: () => setIsPageLoading(false),
    });

    const handleLogout = () => {
        setIsPageLoading(true);
        signOut();
    };

    const handleOpenAboutBox = () => {
        popupManager.open(AboutScreen);
    };

    const handleOpenSettings = () => {
        navigate(settingsModulePaths.automationRules, { state: { from: location.pathname } });
    };

    useEffect(() => {
        if (hasLanguagesError) {
            showErrorNotification(t(GENERIC_SERVER_ERROR_TRANSLATION));
        }
    }, [hasLanguagesError, t]);

    return (
        <>
            <PocBackdrop disableScrollLock opacity={0.5} hidden={!isPageLoading}>
                <PocProgressSpinner variant="primary" size="large" />
            </PocBackdrop>
            <HeaderApp
                onOpenSettings={handleOpenSettings}
                userInSession={auth.userData}
                languages={languages}
                onLogout={handleLogout}
                onOpenAboutBox={handleOpenAboutBox}
                onOpenUserAssistance={handleClickUserAssistance}
                isCheckingUserAssistanceAvailability={isCheckingUserAssistanceAvailability}
                isUserAssistanceAvailable={isUserAssistanceAvailable}
                userAssistanceTooltipKey={userAssistanceTooltipKey}
            />
        </>
    );
};
